import React, { useMemo, useState } from "react";
import { CreateCommanWay, ListCommanWay } from "API/commonApi";
import { useMutation, useQuery } from "@tanstack/react-query";
import Swal from "sweetalert2";
import UseToast from "utils/AfToast";
import Select from "react-select";
import Spinner from "utils/Spinner";
import { Button } from "@heroui/react";
import { useNavigate } from "react-router-dom";

const RATE_FIELDS = [
  { key: "freightRate", label: "Freight" },
  { key: "fuelRate", label: "Fuel" },
  { key: "pkgRate", label: "Packaging" },
  { key: "pickupRate", label: "Pickup" },
  { key: "deliveryRate", label: "Delivery" },
  { key: "odaRate", label: "ODA" },
  { key: "fovRate", label: "FOV" },
  { key: "chcRate", label: "CHC" },
  { key: "abRate", label: "AB" },
  { key: "dgRate", label: "DG" },
  { key: "diceRate", label: "DICE" },
  { key: "insuranceRate", label: "Insurance" },
  { key: "toPayRate", label: "To Pay" },
  { key: "demurrageRate", label: "Demurrage" },
  { key: "otherRate", label: "Other" },
  { key: "dataLoggerRate", label: "Data Logger" },
  { key: "fodRate", label: "FOD" },
  { key: "codRate", label: "COD" },
  { key: "vchcRate", label: "VCHC" },
  { key: "holidayRate", label: "Holiday" },
  { key: "ten_30Rate", label: "10:30" },
];

const emptyAwbRow = () => ({
  awb_no: "",
  freightRate: "",
  fuelRate: "",
  pkgRate: "",
  pickupRate: "",
  deliveryRate: "",
  odaRate: "",
  fovRate: "",
  chcRate: "",
  abRate: "",
  dgRate: "",
  diceRate: "",
  insuranceRate: "",
  toPayRate: "",
  demurrageRate: "",
  otherRate: "",
  dataLoggerRate: "",
  fodRate: "",
  codRate: "",
  vchcRate: "",
  holidayRate: "",
  ten_30Rate: "",
});

const CnDnCreate: React.FC = () => {
  const navigate = useNavigate();

  const [noteType, setNoteType] = useState<"CN" | "DN">("CN");
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [selectedBranch, setSelectedBranch] = useState<any>(null);
  const [reason, setReason] = useState("");
  const [remarks, setRemarks] = useState("");
  const [selectedInvoices, setSelectedInvoices] = useState<any[]>([]);
  const [baseValue, setBaseValue] = useState("");
  const [prefix, setPrefix] = useState("");
  const [awbData, setAwbData] = useState<any[]>([]);

  // Options
  const [customerList, setCustomerList] = useState<any[]>([]);
  const [branchOptions, setBranchOptions] = useState<any[]>([]);
  const [invoiceOptions, setInvoiceOptions] = useState<any[]>([]);

  // Fetch customers
  useQuery(
    ["CustomersListApi_CnDnCreate"],
    () => ListCommanWay("/admin/customer/list", { isActive: true }, { paginate: 1000 }),
    {
      keepPreviousData: true,
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          let opt: any[] = [];
          data.data.data.forEach((p: any) => {
            opt.push({ value: Number(p.id), label: p.code + " - " + p.sh });
          });
          setCustomerList(opt);
        }
      },
    }
  );

  // Fetch branches
  useQuery(
    ["BranchesListApi_CnDnCreate"],
    () => ListCommanWay("/admin/subbranch/list", {}, { paginate: 1000 }),
    {
      keepPreviousData: true,
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          let opt: any[] = [];
          data.data.data.forEach((p: any) => {
            opt.push({ value: p.id, label: p.branch_name });
          });
          setBranchOptions(opt);
        }
      },
    }
  );

  // Fetch invoices for linking
  useQuery(
    ["InvoiceListApi_CnDnCreate"],
    () =>
      ListCommanWay(
        "/admin/inovic_for_c_b/list",
        { customerId: { $ne: "" } },
        { include: ["_customerId", "_subbranch"], paginate: 5000 }
      ),
    {
      keepPreviousData: true,
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          let opt: any[] = [];
          data.data.data.forEach((p: any) => {
            opt.push({
              value: p.id,
              label: `${p._subbranch?.branch_code || ""}${p.invoice_no} - ${p._customerId?.sh || "N/A"} (₹${Number(p.total_value || 0).toFixed(2)})`,
              customerId: p.customerId,
            });
          });
          setInvoiceOptions(opt);
        }
      },
    }
  );

  // Filter invoices by selected customer
  const filteredInvoiceOptions = useMemo(() => {
    if (!selectedCustomer?.value) return invoiceOptions;
    return invoiceOptions.filter((inv) => inv.customerId == selectedCustomer.value);
  }, [invoiceOptions, selectedCustomer]);

  // Create CN/DN mutation
  const { mutate: mutateCreate, isLoading: createLoading } = useMutation(
    (payload: any) =>
      CreateCommanWay({
        url: "/admin/cn-dn/create",
        data: payload,
      }),
    {
      onSuccess: (data: any) => {
        if (data.status == "SUCCESS") {
          const note = data.data;
          Swal.fire({
            title: `${note.note_type === "CN" ? "Credit" : "Debit"} Note Created`,
            html: `
              <div style="text-align: left;">
                <p><strong>Note No:</strong> ${note.note_no}</p>
                <p><strong>Total Value:</strong> ₹${Number(note.total_value).toFixed(2)}</p>
                <p><strong>IRN Status:</strong> <span style="color: ${note.irn_status === "ACTIVE" ? "green" : "#b8860b"}">${note.irn_status}</span></p>
              </div>
            `,
            icon: "success",
            confirmButtonText: "View Details",
            showCancelButton: true,
            cancelButtonText: "Back to List",
          }).then((result) => {
            if (result.isConfirmed) {
              navigate(`/billing/cn-dn/${note.id}`);
            } else {
              navigate("/billing/cn-dn");
            }
          });
        } else {
          UseToast(data.message || "Failed to create note", "error");
        }
      },
      onError: (data: any) => {
        typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
      },
    }
  );

  // AWB helpers
  const addAwbRow = () => setAwbData([...awbData, emptyAwbRow()]);
  const removeAwbRow = (idx: number) => setAwbData(awbData.filter((_, i) => i !== idx));
  const updateAwbField = (idx: number, field: string, value: string) => {
    const updated = [...awbData];
    updated[idx] = { ...updated[idx], [field]: value };
    setAwbData(updated);
  };

  const handleSubmit = () => {
    if (!selectedBranch?.value) {
      UseToast("Please select a subbranch", "error");
      return;
    }
    if (!reason.trim()) {
      UseToast("Please enter a reason", "error");
      return;
    }
    const hasAwb = awbData.length > 0;

    if (!hasAwb && (!baseValue || Number(baseValue) <= 0)) {
      UseToast("Please enter a valid base amount", "error");
      return;
    }

    // If no AWBs, prefix (product code) is required
    if (!hasAwb && !prefix.trim()) {
      UseToast("Please enter Product Code (prefix) when no AWBs are provided", "error");
      return;
    }

    // Validate AWB rows
    if (hasAwb) {
      for (let i = 0; i < awbData.length; i++) {
        if (!awbData[i].awb_no.trim()) {
          UseToast(`AWB #${i + 1}: AWB No is required`, "error");
          return;
        }
      }
    }

    const payload: any = {
      note_type: noteType,
      subbranch: selectedBranch.value,
      reason: reason.trim(),
      remarks: remarks.trim() || undefined,
      base_value: baseValue ? Number(baseValue) : undefined,
    };

    if (hasAwb) {
      payload.awb_data = awbData.map((row) => {
        const obj: any = {
          awb_no: row.awb_no.trim(),
        };
        RATE_FIELDS.forEach(({ key }) => {
          if (row[key] && Number(row[key]) !== 0) {
            obj[key] = Number(row[key]);
          }
        });
        return obj;
      });
    } else {
      payload.prefix = prefix.trim();
    }

    if (selectedCustomer?.value) {
      payload.customerId = selectedCustomer.value;
    }
    if (selectedInvoices.length > 0) {
      payload.invoice_ids = selectedInvoices.map((x: any) => x.value);
    }

    Swal.fire({
      title: `Create ${noteType === "CN" ? "Credit Note" : "Debit Note"}?`,
      html: `<p>This will create a ${noteType} for ₹${Number(baseValue).toFixed(2)}${hasAwb ? ` with <strong>${awbData.length}</strong> AWB(s)` : ""}.</p>`,
      icon: "question",
      confirmButtonText: "Create",
      showCancelButton: true,
    }).then((result) => {
      if (result.isConfirmed) {
        mutateCreate(payload);
      }
    });
  };

  const hasAwb = awbData.length > 0;

  return (
    <>
      {createLoading && <Spinner />}

      <h1 className="text-center my-3 text-lg font-semibold">Create Credit / Debit Note</h1>

      <div className="w-full px-4">
        <div className="border rounded-lg p-6 bg-white shadow-sm">
          {/* Top fields grid - 4 columns */}
          <div className="grid grid-cols-4 sm:grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            {/* Note Type */}
            <div>
              <label className="block mb-2 text-sm font-semibold text-gray-900">Note Type *</label>
              <div className="flex gap-2">
                <label
                  className={`flex items-center gap-1 px-3 py-2 rounded-lg border-2 cursor-pointer text-sm ${
                    noteType === "CN" ? "border-blue-500 bg-blue-50" : "border-gray-200"
                  }`}
                >
                  <input type="radio" name="noteType" value="CN" checked={noteType === "CN"} onChange={() => setNoteType("CN")} />
                  <span className="font-medium">CN</span>
                </label>
                <label
                  className={`flex items-center gap-1 px-3 py-2 rounded-lg border-2 cursor-pointer text-sm ${
                    noteType === "DN" ? "border-orange-500 bg-orange-50" : "border-gray-200"
                  }`}
                >
                  <input type="radio" name="noteType" value="DN" checked={noteType === "DN"} onChange={() => setNoteType("DN")} />
                  <span className="font-medium">DN</span>
                </label>
              </div>
            </div>
            <div>
              <label className="block mb-2 text-sm font-semibold text-gray-900">Customer (optional)</label>
              <Select
                className="bg-gray-50 border-gray-300 text-gray-900 text-sm rounded-lg block"
                options={customerList}
                onChange={(e: any) => {
                  setSelectedCustomer(e);
                  setSelectedInvoices([]);
                }}
                value={selectedCustomer}
                isClearable={true}
                placeholder="Select customer..."
              />
            </div>

            {/* Subbranch */}
            <div>
              <label className="block mb-2 text-sm font-semibold text-gray-900">Subbranch *</label>
              <Select
                className="bg-gray-50 border-gray-300 text-gray-900 text-sm rounded-lg block"
                options={branchOptions}
                onChange={(e: any) => setSelectedBranch(e)}
                value={selectedBranch}
                isClearable={false}
                placeholder="Select subbranch..."
              />
            </div>

            {/* Base Amount */}
            <div>
              <label className="block mb-2 text-sm font-semibold text-gray-900">Base Amount (₹) {!hasAwb && "*"}</label>
              <input
                type="number"
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                placeholder="e.g. 5000"
                value={baseValue}
                onChange={(e) => setBaseValue(e.target.value)}
                min="0"
                step="0.01"
              />
            </div>

            {/* Reason */}
            <div>
              <label className="block mb-2 text-sm font-semibold text-gray-900">Reason *</label>
              <input
                type="text"
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                placeholder="e.g. Rate correction, Damaged goods"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>

            {/* Product Code (prefix) - required when no AWBs */}
            {!hasAwb && (
              <div>
                <label className="block mb-2 text-sm font-semibold text-gray-900">Product Code (prefix) *</label>
                <input
                  type="text"
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                  placeholder="e.g. D, E, S"
                  value={prefix}
                  onChange={(e) => setPrefix(e.target.value)}
                />
                <p className="text-xs text-gray-500 mt-1">Required when no AWBs are added</p>
              </div>
            )}

            {/* Linked Invoices */}
            <div>
              <label className="block mb-2 text-sm font-semibold text-gray-900">Linked Invoices (optional)</label>
              <Select
                className="bg-gray-50 border-gray-300 text-gray-900 text-sm rounded-lg block"
                options={filteredInvoiceOptions}
                onChange={(e: any) => setSelectedInvoices(e || [])}
                value={selectedInvoices}
                isMulti={true}
                isClearable={true}
                placeholder={selectedCustomer ? "Select invoices..." : "Select customer first or search all..."}
              />
            </div>

            {/* Remarks */}
            <div>
              <label className="block mb-2 text-sm font-semibold text-gray-900">Remarks (optional)</label>
              <input
                type="text"
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                placeholder="Additional notes..."
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </div>
          </div>

          {/* AWB Data Section */}
          <div className="mt-5 border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-semibold text-gray-900">
                AWB Data {hasAwb && `(${awbData.length})`}
              </label>
              <button
                type="button"
                onClick={addAwbRow}
                className="px-3 py-1 bg-blue-600 text-white text-xs rounded font-semibold"
              >
                + Add AWB
              </button>
            </div>

            {!hasAwb && (
              <p className="text-xs text-gray-500">No AWBs added. Product Code (prefix) will be used instead.</p>
            )}

            {awbData.map((row, idx) => (
              <div key={idx} className="border rounded p-2 mb-2 bg-gray-50">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold text-gray-700 w-8">#{idx + 1}</span>
                  <input
                    type="text"
                    className="bg-white border border-gray-300 text-gray-900 text-xs rounded p-1.5 w-40"
                    placeholder="AWB No *"
                    value={row.awb_no}
                    onChange={(e) => updateAwbField(idx, "awb_no", e.target.value)}
                  />
                  {RATE_FIELDS.map(({ key, label }) => (
                    <input
                      key={key}
                      type="number"
                      className="bg-white border border-gray-300 text-gray-900 text-xs rounded p-1.5 w-20"
                      placeholder={label}
                      title={label}
                      value={row[key]}
                      onChange={(e) => updateAwbField(idx, key, e.target.value)}
                      min="0"
                      step="0.01"
                    />
                  ))}
                  <button
                    type="button"
                    onClick={() => removeAwbRow(idx)}
                    className="px-2 py-1 bg-red-500 text-white text-xs rounded ml-auto"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex gap-3 justify-end mt-5">
            <Button
              type="button"
              className="font-semibold font-inter"
              radius="sm"
              color="default"
              variant="bordered"
              onPress={() => navigate("/billing/cn-dn")}
            >
              Cancel
            </Button>
            <Button type="button" className="font-semibold font-inter text-white" radius="sm" color="primary" onPress={handleSubmit}>
              Create {noteType === "CN" ? "Credit Note" : "Debit Note"}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CnDnCreate;
